import debounce from 'lodash.debounce';
import { useEffect, useRef, useState } from 'react';

import { request } from '~/api/_utils/request';
import { usePaginationIntersect } from '~/api/_utils/usePaginationSWR';
import { ApiOrganization } from '~/api/v1/organizations/get';
import { useFetch } from '~/hooks/useFetch';

const START_PAGE = 1;
const PER_PAGE = 12;

const getPath = (params: SearchOrganizationsParams): string => {
  const q = new URLSearchParams();

  q.append('page', String(params.page ?? START_PAGE));
  if (params.keyword) q.append('keyword', params.keyword);
  params.locations?.forEach((l) => q.append('locations[]', l));
  params.social_issues?.forEach((s) => q.append('social_issues[]', s));
  params.sdgs?.forEach((s) => q.append('sdgs[]', String(s)));

  return `/v2/searches/organizations?${q.toString()}`;
};

export type SearchOrganizationsParams = {
  locations?: string[];
  social_issues?: string[];
  sdgs?: number[];
  keyword?: string;
  page?: number;
};

export type ApiOrganizationPicked = Pick<
  ApiOrganization,
  | 'area'
  | 'category'
  | 'cover'
  | 'id'
  | 'logo'
  | 'name'
  | 'sdgs'
  | 'tags'
  | 'uid'
  | 'vision'
  | 'tags'
  | 'prefecture'
>;

export async function apiGetV2SearchOrganizations(params: SearchOrganizationsParams) {
  const { organizations, total_count } = await request.GET<{
    organizations: ApiOrganizationPicked[];
    total_count: number;
  }>(getPath(params));
  return { organizations, totalCount: total_count };
}

export function useApiGetV2SearchOrganizations(params: SearchOrganizationsParams) {
  const pageRef = useRef(START_PAGE);
  const isDoneRef = useRef(false);

  const [orgs, setOrgs] = useState<ApiOrganizationPicked[]>();
  const [totalCount, setTotalCount] = useState<number>(0);
  const { fetch, isLoading } = useFetch();

  const fetchOrgs = debounce(async () => {
    const result = await fetch(() => apiGetV2SearchOrganizations(params));
    setOrgs(result.organizations);
    setTotalCount(result.totalCount);
    if (result.organizations.length < PER_PAGE) {
      isDoneRef.current = true;
    }
  }, 300);

  // 初回 or params が変わった場合、選択中のページネーション番号に対応する orgs を fetch する
  useEffect(() => {
    pageRef.current = START_PAGE;
    isDoneRef.current = false;
    setOrgs(undefined);
    fetchOrgs();
  }, [params]);

  const initializeObserver = usePaginationIntersect(() => {
    if (isDoneRef.current) return;
    pageRef.current++;
    fetchOrgs();
  });

  return { orgs, totalCount, isLoading, initializeObserver };
}
